import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { AttributeValueListParams, AttributeValueStatusFilter } from '@/types/attribute-value';

export interface AttributeValueQueryState {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search: string;
  status: AttributeValueStatusFilter;
  dateFrom: string;
  dateTo: string;
  attributeId: string;
}

type ParamPatch = Partial<{
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search: string | null;
  status: AttributeValueStatusFilter | null;
  dateFrom: string | null;
  dateTo: string | null;
  attributeId: string | null;
}>;

const DEFAULTS: AttributeValueQueryState = {
  page: 1,
  limit: 10,
  sortBy: 'createdAt',
  sortOrder: 'desc',
  search: '',
  status: 'all',
  dateFrom: '',
  dateTo: '',
  attributeId: '',
};

function parseStatus(value: string | null): AttributeValueStatusFilter {
  if (value === 'active' || value === 'inactive') return value;
  return 'all';
}

export function useAttributeValueQueryParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo<AttributeValueQueryState>(() => {
    const page = Number(searchParams.get('page') || DEFAULTS.page);
    const limit = Number(searchParams.get('limit') || DEFAULTS.limit);

    return {
      page: Number.isFinite(page) && page > 0 ? page : DEFAULTS.page,
      limit: Number.isFinite(limit) && limit > 0 ? Math.min(limit, 100) : DEFAULTS.limit,
      sortBy: searchParams.get('sortBy') || DEFAULTS.sortBy,
      sortOrder: searchParams.get('sortOrder') === 'asc' ? 'asc' : 'desc',
      search: searchParams.get('search') || '',
      status: parseStatus(searchParams.get('status')),
      dateFrom: searchParams.get('dateFrom') || '',
      dateTo: searchParams.get('dateTo') || '',
      attributeId: searchParams.get('attributeId') || '',
    };
  }, [searchParams]);

  const apiParams = useMemo<AttributeValueListParams>(
    () => ({
      page: state.page,
      limit: state.limit,
      sortBy: state.sortBy,
      sortOrder: state.sortOrder,
      ...(state.search ? { search: state.search } : {}),
      ...(state.status !== 'all' ? { status: state.status } : {}),
      ...(state.dateFrom ? { dateFrom: state.dateFrom } : {}),
      ...(state.dateTo ? { dateTo: state.dateTo } : {}),
      ...(state.attributeId ? { attributeId: state.attributeId } : {}),
    }),
    [state],
  );

  const patchParams = useCallback(
    (patch: ParamPatch, options?: { resetPage?: boolean }) => {
      setSearchParams(
        (searchParams) => {
          const next = new URLSearchParams(searchParams);
          const shouldResetPage = options?.resetPage ?? false;

          if (shouldResetPage) {
            next.set('page', '1');
          }

          const apply = (
            key: string,
            value: string | null | undefined,
            omitValues: string[] = [],
          ) => {
            if (value === null || value === undefined || value === '' || omitValues.includes(value)) {
              next.delete(key);
              return;
            }
            next.set(key, value);
          };

          if (patch.page !== undefined) next.set('page', String(patch.page));
          if (patch.limit !== undefined) next.set('limit', String(patch.limit));
          if (patch.sortBy !== undefined) next.set('sortBy', patch.sortBy);
          if (patch.sortOrder !== undefined) next.set('sortOrder', patch.sortOrder);
          if (patch.search !== undefined) apply('search', patch.search);
          if (patch.status !== undefined) apply('status', patch.status, ['all']);
          if (patch.dateFrom !== undefined) apply('dateFrom', patch.dateFrom);
          if (patch.dateTo !== undefined) apply('dateTo', patch.dateTo);
          if (patch.attributeId !== undefined) apply('attributeId', patch.attributeId);

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const resetAll = useCallback(() => {
    setSearchParams(
      (searchParams) => {
        const next = new URLSearchParams(searchParams);
        next.delete('search');
        next.delete('status');
        next.delete('dateFrom');
        next.delete('dateTo');
        next.delete('attributeId');
        next.delete('sortBy');
        next.delete('sortOrder');
        next.set('page', '1');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const hasActiveFiltersOrSort = useMemo(
    () =>
      state.search !== '' ||
      state.status !== 'all' ||
      state.dateFrom !== '' ||
      state.dateTo !== '' ||
      state.attributeId !== '' ||
      state.sortBy !== DEFAULTS.sortBy ||
      state.sortOrder !== DEFAULTS.sortOrder,
    [state],
  );

  return { state, apiParams, patchParams, resetAll, hasActiveFiltersOrSort };
}
